import React, { ComponentProps, ReactNode, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { BusinessStatus } from '../../mappers/business/toBusinessModel';
import {
    canEditBusinessAvatar,
    canEditBusinessCaver,
    canRegisterBusiness,
    canSeeBusinessSettings,
    canViewBusinessAsAdmin,
    hasAccessToBusinessStat,
    isBusinessAdmin,
} from '../../utils/accessService';
import { addProtocol } from '../../utils/addProtocol';
import { cropPublicId } from '../../utils/cropPublicId';
import { formatText } from '../../utils/format/formatText';
import { ymTrackSettingsClick, ymTrackStatisticsClick } from '../../utils/metrika';
import { pluralize } from '../../utils/pluralize';
import { down } from '../../utils/styles/breakpoints';
import { useTranslation } from '../../utils/useTranslation';
import { Skeleton } from '../feedback/Skeleton';
import { IconCamera } from '../icon/IconCamera';
import { IconKebab } from '../icon/IconKebab';
import { IconNewTab } from '../icon/IconNewTab';
import { Button } from '../input/Button';
import { IconButton } from '../input/IconButton';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Avatar } from '../media/Avatar';
import { Image } from '../media/Image';
import { MediaCover } from '../media/MediaCover';
import { Menu, MenuItem } from '../navigation/Menu';
import { ReactComponent as BusinessCoverSvg } from '../resources/defaultCover.svg';
import { Typography } from '../typography/Typography';
import { TypographyExpander } from '../typography/TypographyExpander';
import { UserUI } from '../users/types';
import { BlockedStatus } from './BlockedStatus';
import { BusinessStatistic, BusinessStatisticSkeleton } from './BusinessStatistic';
import { StatusBadge } from './StatusBadge';
import type { BusinessStatsUI, BusinessUI } from './types';

const BUSINESS_COVER_RATIO = 16 / 9;

const BusinessCardRoot = styled(Box).attrs({
    width: '100%',
    overflow: 'hidden',
    b: { sm: 3 },
})``;

const BusinessCardContent = styled(Box).attrs({
    position: 'relative',
    background: 'var(--background-primary)',
    pb: 0,
})``;

const BusinessCardRow = styled(Flex).attrs({
    width: '100%',
    py: 1,
    px: { xs: 2, sm: 3 },
})``;

const Properties = styled(Flex).attrs((props) => ({
    ...props,
    direction: 'row',
    alignItems: 'center',
    spacing: { sm: 2 },
}))`
    ${down('sm')} {
        & > *:not(:last-child)::after {
            content: '•';
            display: inline-block;
            margin-left: 4px;
            margin-right: 8px;
            color: var(--color-primary);
        }
    }
`;

interface PropertyProps {
    value: number;
    displayName: string;
}

const Property = (props: PropertyProps) => (
    <Flex
        direction={{ xs: 'row', sm: 'column' }}
        alignItems={{ sm: 'flex-end' }}
        spacing={{ xs: 0.5, sm: 0 }}
    >
        <Typography weight="medium">{props.value}</Typography>
        <Typography weight="medium">{props.displayName}</Typography>
    </Flex>
);

const PropertySkeleton = () => (
    <Flex
        direction={{ xs: 'row', sm: 'column' }}
        alignItems={{ sm: 'flex-end' }}
        spacing={{ xs: 0.5, sm: 0 }}
    >
        <Skeleton variant="text" width={60} height={20} />
        <Skeleton variant="text" width={100} height={20} />
    </Flex>
);

interface BusinessCardProps extends ComponentProps<typeof Box> {
    business: BusinessUI;
    user?: UserUI;
    onPersistBusinessCover?: VoidFunction;
    onPersistBusinessAvatar?: VoidFunction;
    onShare: (businessUI: BusinessUI) => void;
    onSubscribe: (businessUI: BusinessUI) => void;
    children?: ReactNode;
    statisticLoading: boolean;
    businessStatistic?: {
        stats: BusinessStatsUI;
    };
    onReport?: VoidFunction;
    onBadgeClick: VoidFunction;
}

export const BusinessCard = (props: BusinessCardProps) => {
    const {
        business,
        user,
        children,
        onShare: onBaseShare,
        onSubscribe: onBaseSubscribe,
        onPersistBusinessCover,
        onPersistBusinessAvatar,
        statisticLoading,
        businessStatistic,
        onReport,
        onBadgeClick,
        ...other
    } = props;
    const t = useTranslation();
    const navigate = useNavigate();

    const onShare = useCallback(() => onBaseShare(business), [business, onBaseShare]);
    const onSubscribe = useCallback(() => onBaseSubscribe(business), [business, onBaseSubscribe]);
    const onSettingsClick = useCallback(() => {
        ymTrackSettingsClick({
            page: 'Профиль бизнеса',
            userLogin: user?.login,
            businessId: user?.profile?.publicId,
        });
    }, [user?.login, user?.profile?.publicId]);

    const onAllStatisticClick = useCallback(() => {
        ymTrackStatisticsClick({
            page: 'Профиль бизнеса',
            userLogin: user?.login,
            businessId: user?.profile?.publicId,
        });
    }, [user?.login, user?.profile?.publicId]);

    return (
        <BusinessCardRoot {...other}>
            <Box position="relative">
                {(!isBusinessAdmin(business.id, user) ||
                    canRegisterBusiness(user?.businessRegistrationStatus)) && (
                    <Box zIndex={10} position="absolute" top={0} right={0}>
                        <Menu
                            pt={1}
                            pr={1}
                            trigger={
                                <IconButton>
                                    <IconKebab />
                                </IconButton>
                            }
                        >
                            {canRegisterBusiness(user?.businessRegistrationStatus) ? (
                                <MenuItem onClick={() => navigate('/businesses/registration')}>
                                    <Typography>{t('Подтвердить права на профиль')}</Typography>
                                </MenuItem>
                            ) : undefined}
                            {!isBusinessAdmin(business.id, user) ? (
                                <MenuItem onClick={onReport}>
                                    <Typography noWrap color="error">
                                        {t('Пожаловаться')}
                                    </Typography>
                                </MenuItem>
                            ) : undefined}
                        </Menu>
                    </Box>
                )}
                <MediaCover ratio={BUSINESS_COVER_RATIO}>
                    {business.coverUrl ? <Image src={business.coverUrl} /> : <BusinessCoverSvg />}
                </MediaCover>
                {canEditBusinessCaver(business.id, user) && (
                    <Button
                        size="s"
                        position="absolute"
                        bottom={{ xs: 1, sm: 2 }}
                        right={{ xs: 2, sm: 3 }}
                        onClick={onPersistBusinessCover}
                    >
                        {t('Изменить обложку')}
                    </Button>
                )}
            </Box>
            <BusinessCardContent>
                <Box pb={1}>
                    <BusinessCardRow
                        spacing={{ xs: 1, sm: 2 }}
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ sm: 'center' }}
                        justifyContent={{ sm: 'space-between' }}
                    >
                        <Flex direction="row" alignItems="center" spacing={2}>
                            <Box position="relative">
                                <Avatar
                                    mt={-3.5}
                                    size={{ xs: 100, sm: 108 }}
                                    src={business.avatarUrl}
                                    title={business.displayName}
                                    border="1px solid var(--divider)"
                                    cursor={
                                        canEditBusinessAvatar(business.id, user)
                                            ? 'pointer'
                                            : 'default'
                                    }
                                    onClick={
                                        canEditBusinessAvatar(business.id, user)
                                            ? onPersistBusinessAvatar
                                            : undefined
                                    }
                                />
                                {canEditBusinessAvatar(business.id, user) && (
                                    <Box
                                        position="absolute"
                                        pointerEvents="none"
                                        bottom={0}
                                        right={0}
                                        p={1}
                                        borderRadius="50%"
                                        background="var(--control-secondary-background)"
                                        backdropFilter="blur(var(--blur-radius-l))"
                                    >
                                        <IconCamera
                                            size={20}
                                            color="var(--control-secondary-color)"
                                        />
                                    </Box>
                                )}
                            </Box>
                            <Box>
                                <Typography variant="title2" weight="bold">
                                    {cropPublicId(business.publicId)}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="secondary"
                                    pb={{ xs: 0.5, sm: 1 }}
                                >
                                    {business.displayName}
                                </Typography>
                                {business.status && (
                                    <StatusBadge
                                        businessStatus={business.status}
                                        statusDisplay={business.statusDisplay}
                                        onClick={onBadgeClick}
                                    />
                                )}
                            </Box>
                        </Flex>
                        <Properties>
                            <Property
                                value={business.posts ?? 0}
                                displayName={pluralize(
                                    business.posts ?? 0,
                                    'пост',
                                    'поста',
                                    'постов',
                                )}
                            />
                            {business.subscribers ? (
                                <Property
                                    value={business.subscribers ?? 0}
                                    displayName={pluralize(
                                        business.subscribers ?? 0,
                                        'подписчик',
                                        'подписчика',
                                        'подписчиков',
                                    )}
                                />
                            ) : (
                                <></>
                            )}
                        </Properties>
                    </BusinessCardRow>
                    {business.status === BusinessStatus.DECLINED && (
                        <BlockedStatus login={user?.login} />
                    )}
                    {hasAccessToBusinessStat(business.id, user) &&
                        (statisticLoading ? (
                            <BusinessStatisticSkeleton />
                        ) : (
                            businessStatistic?.stats && (
                                <BusinessStatistic
                                    statistic={businessStatistic.stats}
                                    statisticUrl={business.statisticUrl}
                                    onAllStatisticClick={onAllStatisticClick}
                                />
                            )
                        ))}
                    <BusinessCardRow direction="row" spacing={1}>
                        <Button flexGrow={1} variant="filled" color="secondary" onClick={onShare}>
                            Поделиться
                        </Button>
                        {canSeeBusinessSettings(business.id, user) ? (
                            <Button
                                as={Link}
                                flexGrow={1}
                                variant="filled"
                                color="secondary"
                                to={business.editLink}
                                onClick={onSettingsClick}
                            >
                                Настроить
                            </Button>
                        ) : (
                            <Button
                                flexGrow={1}
                                variant="filled"
                                color={business.subscribed ? 'secondary' : 'primary'}
                                onClick={onSubscribe}
                            >
                                {business.subscribed ? 'Вы подписаны' : 'Подписаться'}
                            </Button>
                        )}
                    </BusinessCardRow>
                    <BusinessCardRow display="block">
                        {business.description && (
                            <TypographyExpander
                                width="100%"
                                color="primary"
                                lines={canViewBusinessAsAdmin(business.id, user) ? 2 : 4}
                                backgroundColor="var(--background-primary)"
                            >
                                {formatText(business.description)}
                            </TypographyExpander>
                        )}
                    </BusinessCardRow>
                    {business.links?.site && (
                        <BusinessCardRow>
                            <Button
                                as="a"
                                href={addProtocol(business.links?.site)}
                                target="_blank"
                                rel="nofollow noreferrer"
                                variant="filled"
                                color="secondary"
                                spacing={1}
                            >
                                {business.links?.shortSite ||
                                    business.links?.siteDisplay ||
                                    business.links?.site}
                                <IconNewTab fontSize={12} color="inherit" />
                            </Button>
                        </BusinessCardRow>
                    )}
                </Box>

                {children}
            </BusinessCardContent>
        </BusinessCardRoot>
    );
};

interface BusinessCardSkeletonProps extends ComponentProps<typeof Box> {}

export const BusinessCardSkeleton = (props: BusinessCardSkeletonProps) => (
    <BusinessCardRoot {...props}>
        <Skeleton variant="rect" ratio={BUSINESS_COVER_RATIO} />
        <BusinessCardContent>
            <Box pb={1}>
                <BusinessCardRow
                    spacing={{ xs: 1, sm: 2 }}
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ sm: 'center' }}
                    justifyContent={{ sm: 'space-between' }}
                >
                    <Flex direction="row" alignItems="center" spacing={2}>
                        <Skeleton
                            variant="circular"
                            width={{ xs: 100, sm: 108 }}
                            height={{ xs: 100, sm: 108 }}
                            mt={-3.5}
                        />
                        <Box>
                            <Skeleton variant="text" width={120} height={26} />
                            <Skeleton variant="text" width={100} height={14} />
                        </Box>
                    </Flex>

                    <Properties>
                        <PropertySkeleton />
                        <PropertySkeleton />
                    </Properties>
                </BusinessCardRow>
                <BusinessCardRow direction="row" spacing={1}>
                    <Skeleton variant="rect" width="50%" height={32} borderRadius="999px" />
                    <Skeleton variant="rect" width="50%" height={32} borderRadius="999px" />
                </BusinessCardRow>
                <BusinessCardRow spacing={1}>
                    <Skeleton variant="text" width="100%" height={20} />
                    <Skeleton variant="text" width="30%" height={20} />
                </BusinessCardRow>
                <BusinessCardRow direction="row" spacing={1}>
                    <Skeleton variant="rect" width={120} borderRadius="999px" height={32} />
                </BusinessCardRow>
            </Box>
        </BusinessCardContent>
    </BusinessCardRoot>
);
